import { Component, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { ModalService } from 'src/app/services/modal/modal.service';
import { MultiprofessionalMeetingService } from 'src/app/services/multiprofessional-meeting/multiprofessional-meeting.service';
import { HeaderCommunicationService } from 'src/app/services/node/header-communication.service';
import { Header } from 'src/app/utils/interfaces/new/header.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { ReunionPluriprofessionnelle } from 'src/app/utils/interfaces/new/ReunionPluriprofessionnelle';
import * as moment from 'moment';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';

interface MeetingTableDisplay {
  dateMeeting: Date;
  hourStart: string;
  hourEnd: string;
  title: string;
  coordinator: string;
  modifieLe: string;
  status: string;
  id: string;
  meta: ReunionPluriprofessionnelle;
}

@Component({
  selector: 'app-multiprofessional-meeting',
  templateUrl: './multiprofessional-meeting.component.html',
  styleUrls: ['./multiprofessional-meeting.component.scss']
})
export class MultiprofessionalMeetingComponent implements OnInit, OnDestroy {

  private _meetings: Paginated<ReunionPluriprofessionnelle>;
  public meetingsTable: Paginated<MeetingTableDisplay>;
  public meetingsTableColumnTable = [
    { title: 'Date', data: 'dateMeeting' },
    { title: 'Début', data: 'hourStart' },
    { title: 'Fin', data: 'hourEnd' },
    { title: 'Titre', data: 'title' },
    { title: 'Coordinateur', data: 'coordinator' },
    { title: 'Modifié le', data: 'modifieLe' },
    { title: 'Statut', data: 'status' },
    { title: 'Terminer', data: null },
    { title: 'Supprimer', data: null },
  ];
  public formGroup: FormGroup;
  private formGroupChangeSub: Subscription;
  private _tableFiltre: Filtres = { currentPage: 1, perpage: 10, sortBy: 'createdAt' };

  constructor(
    private _mpmSrv: MultiprofessionalMeetingService,
    private _modalSrv: ModalService,
    private _viewContainerRef: ViewContainerRef,
    private _send: HeaderCommunicationService,
    private _router: Router,
    private _formBuilder: FormBuilder
  ) {
    const head: Header = {
      backPage: 'utilitaires',
      isBack: true,
      title: 'Réunions pluriprofessionnelles',
    };
    this._send.announceHeader(head);

    this.formGroup = this._formBuilder.group({
      showOnlyInProgress: [false]
    });

    this.formGroupChangeSub = this.formGroup.get('showOnlyInProgress').valueChanges.subscribe((value) => {
      this._initTableDisplay(value);
    });
  }

  async ngOnInit(): Promise<void> {
    await this.initData();
  }

  ngOnDestroy(): void {
    if (this.formGroupChangeSub) {
      this.formGroupChangeSub.unsubscribe();
    }
  }

  public goToMeetingEdit(event: any): void {
    this._router.navigateByUrl(`user/reunions/edit/${event.id}`);
  }

  public async validMeeting(event: MeetingTableDisplay): Promise<void>{
    const loading = this._modalSrv.openLoading(this._viewContainerRef);

    try {
      const meeting = this._mpmSrv.convertMeetingToCreationMeeting(event.meta);
      meeting.estTermine = !meeting.estTermine;
      const updatedMeeting = await this._mpmSrv.updateMultiprofessionnalMeetings(event.id, meeting);
      this.meetingsTable.objects.splice(
        this.meetingsTable.objects.findIndex((pi) => pi.id === updatedMeeting.id),
        1,
        this.convertMeetingToListElem(updatedMeeting));
    } catch (e) {
      this._showAlert('Une erreur est survenue lors de la validation de cette réunion');
    } finally {
      this._modalSrv.close(loading);
    }
  }

  public async deleteMeeting(event: MeetingTableDisplay): Promise<void>{
    const loading = this._modalSrv.openLoading(this._viewContainerRef);

    try {
      const modal = this._modalSrv.openModalConfirmation(this._viewContainerRef, 'Confirmation', 'Voulez-vous supprimer cette réunion ?');
      modal.instance.closeEmitter.subscribe(async (res: boolean) => {
        if (res) {
          await this._mpmSrv.deleteMultiprofessionnalMeetings(event.id);
          this.meetingsTable.objects.splice(this.meetingsTable.objects.findIndex((p) => p.id === event.id), 1);
        }
      });
    } catch (e) {
      this._showAlert('Une erreur est survenue lors de la suppression de cette réunion');
    } finally {
      this._modalSrv.close(loading);
    }
  }

  public addMeeting(): void {
    this._router.navigateByUrl(`user/reunions/edit`);
  }

  private async initData(): Promise<void> {
    const loading = this._modalSrv.openLoading(this._viewContainerRef);

    try {
      this._meetings = await this._getMeetings();
      this._initTableDisplay();
    } catch (e) {
      this._showAlert();
    } finally {
      this._modalSrv.close(loading);
    }
  }

  public async changePage(page: number): Promise<void> {
    const loading = this._modalSrv.openLoading(this._viewContainerRef);

    try {
      this._tableFiltre.currentPage = page;
      this._meetings = await this._getMeetings();
      this._initTableDisplay();
    } catch (e) {
      this._showAlert();
    } finally {
      this._modalSrv.close(loading);
    }
  }

  public async sortMeetings(sort: any): Promise<void> {
    this._initTableDisplay();
    const loading = this._modalSrv.openLoading(this._viewContainerRef);

    try {
      this._tableFiltre.currentPage = 1;
      this._tableFiltre.sortBy = sort.sortBy;
      this._tableFiltre.sortOrder = sort.sortOrder;
      this._meetings = await this._getMeetings();
    } catch (e) {
      this._showAlert();
    } finally {
      this._modalSrv.close(loading);
    }
  }

  private _initTableDisplay(showOnlyInProgress = false): void {
    const meetingsTmp: Array<MeetingTableDisplay> = [];
    if (this._meetings && this._meetings.objects) {
      for (const m of this._meetings.objects) {
        if (showOnlyInProgress && !m.estTermine) {
          meetingsTmp.push(this.convertMeetingToListElem(m));
        } else if (!showOnlyInProgress) {
          meetingsTmp.push(this.convertMeetingToListElem(m));
        }
      }

      if (this.meetingsTable) {
        this.meetingsTable.objects = [...meetingsTmp];
        this.meetingsTable.page = this._meetings.page;
        this.meetingsTable.perpage = this._meetings.perpage;
        this.meetingsTable.total = this._meetings.total;
      } else {
        this.meetingsTable = {
          objects: meetingsTmp,
          page: this._meetings.page,
          perpage: this._meetings.perpage,
          total: this._meetings.total
        };
      }
    }
  }

  private convertMeetingToListElem(meeting: ReunionPluriprofessionnelle): MeetingTableDisplay {
    return {
      dateMeeting: meeting.dateDebut,
      hourStart: meeting.dateDebut && meeting.dateFin ? moment(meeting.dateDebut).format('HH:mm') : '',
      hourEnd: meeting.dateDebut && meeting.dateFin ? moment(meeting.dateFin).format('HH:mm') : '',
      title: meeting.titre ? meeting.titre : '',
      coordinator: meeting.coordinateur.nom,
      modifieLe: `${moment(meeting.dateModification).format('DD/MM/YYYY')} à ${moment(meeting.dateModification).format('HH')}h${moment(meeting.dateModification).format('mm')}`,
      status: meeting.estTermine ? 'Terminé' : 'En cours',
      id: meeting.id,
      meta: meeting
    };
  }

  private async _getMeetings(): Promise<Paginated<ReunionPluriprofessionnelle>> {
    return new Promise<Paginated<ReunionPluriprofessionnelle>>(async (resolve, reject) => {
      try {
        const currentProfilStr = localStorage.getItem('currentProfil');
        const currentProfil = JSON.parse(currentProfilStr);
        if (currentProfil && currentProfil.id) {
          const meetings = await this._mpmSrv.getAllMultiprofessionnalMeetings(currentProfil.id, this._tableFiltre);
          resolve(meetings);
        }
      } catch (e) {
        reject(e);
      }
    });
  }

  private _showAlert(msg?: string): void {
    msg = msg ? msg : 'Une erreur est survenue lors de la récupération des informations de cette page';

    this._modalSrv.openAlert(this._viewContainerRef, 'Erreur', msg);
  }

}
